import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { candidateChoicesKeys } from "@/application/query-keys/candidate-choices.keys";
import {
  candidateChoicesRepository,
  type UpsertCandidateChoiceInput,
} from "@/infrastructure/repositories/candidate-choices.supabase.repository";
import type { CandidateChoiceRow } from "@/infrastructure/supabase/database.types";

export function useAllCandidateChoices() {
  return useQuery({
    queryKey: candidateChoicesKeys.list(),
    queryFn: () => candidateChoicesRepository.list(),
  });
}

export function useCandidateChoices(candidateId: string | undefined) {
  return useQuery({
    queryKey: candidateChoicesKeys.byCandidate(candidateId ?? ""),
    queryFn: () =>
      candidateId ? candidateChoicesRepository.listByCandidate(candidateId) : Promise.resolve([]),
    enabled: typeof candidateId === "string" && candidateId.length > 0,
  });
}

export function useUpsertCandidateChoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertCandidateChoiceInput) => candidateChoicesRepository.upsert(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: candidateChoicesKeys.all });
      const listKey = candidateChoicesKeys.list();
      const byKey = candidateChoicesKeys.byCandidate(input.candidate_id);
      const prevList = qc.getQueryData<CandidateChoiceRow[]>(listKey);
      const prevBy = qc.getQueryData<CandidateChoiceRow[]>(byKey);
      const now = new Date().toISOString();
      const optimistic: CandidateChoiceRow = {
        id: `optimistic-${input.candidate_id}-${input.component_id}`,
        candidate_id: input.candidate_id,
        component_id: input.component_id,
        product_id: input.product_id,
        override_notes_md: input.override_notes_md,
        created_at: now,
        updated_at: now,
      };
      const replace = (cur: CandidateChoiceRow[] | undefined) => {
        const base = cur ?? [];
        const filtered = base.filter(
          (r) => !(r.candidate_id === input.candidate_id && r.component_id === input.component_id)
        );
        return [optimistic, ...filtered];
      };
      qc.setQueryData<CandidateChoiceRow[]>(listKey, replace);
      qc.setQueryData<CandidateChoiceRow[]>(byKey, replace);
      return { prevList, prevBy };
    },
    onError: (_err, input, ctx) => {
      if (ctx?.prevList) qc.setQueryData(candidateChoicesKeys.list(), ctx.prevList);
      if (ctx?.prevBy)
        qc.setQueryData(candidateChoicesKeys.byCandidate(input.candidate_id), ctx.prevBy);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: candidateChoicesKeys.all });
    },
  });
}

export function useRemoveCandidateChoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, componentId }: { candidateId: string; componentId: string }) =>
      candidateChoicesRepository.remove(candidateId, componentId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: candidateChoicesKeys.all });
    },
  });
}
